/**
 * Staff user detail API service
 *
 * The 401 handling deliberately matches adminService: clear the stored session
 * and send the operator to login. 403 is different — it means the permission
 * was revoked, and the page renders a 404 rather than leaking that the route
 * exists, so it needs its own error type to distinguish it from a generic
 * failure.
 */

import { API_BASE_URL, getAuthHeaders } from './api';
import { raiseSessionExpired } from './sessionExpiry';
import { readBody, messageFrom } from './httpResponse';

export class ForbiddenError extends Error {
  constructor(message = 'Forbidden') {
    super(message);
    this.name = 'ForbiddenError';
  }
}

// Re-exported so existing callers that import SessionExpiredError from here
// keep working; the class itself now lives in sessionExpiry.js alongside the
// 401 handling shared with adminService.
export { SessionExpiredError } from './sessionExpiry';

/**
 * Turn a non-OK response into the right error. Always throws — including for
 * 401, which redirects to /login first and then throws anyway, so a caller's
 * ordinary catch block has something to catch rather than a magic return
 * value to remember to check.
 *
 * The body is read through httpResponse rather than as raw text. These
 * endpoints (admin_ctrl.go, role_ctrl.go) answer with `c.String(...)` today,
 * so nothing changes for them now — but the 2FA surface has already moved to
 * the `{error, message}` envelope, and reading raw text is exactly what breaks
 * on the day these follow. readBody handles either shape, and messageFrom
 * declines to show a message that did not arrive inside the envelope that
 * promises it is fit to display.
 */
const raise = async (response, messages = {}) => {
  if (response.status === 401) {
    raiseSessionExpired();
  }

  if (response.status === 403) {
    throw new ForbiddenError();
  }

  const specific = messages[response.status];
  if (specific) {
    throw new Error(specific);
  }

  const body = await readBody(response);
  throw new Error(messageFrom(body, `HTTP ${response.status}`));
};

export const getUserDetail = async (id) => {
  const response = await fetch(`${API_BASE_URL}/admin/users/${encodeURIComponent(id)}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    // A non-numeric :id also answers 400 ("user id must be a number") — that
    // reads the same as an unknown id to an operator, so it maps to the same
    // message rather than surfacing a raw HTTP 400.
    await raise(response, { 400: 'User not found', 404: 'User not found' });
  }

  return response.json();
};

export const getRoles = async () => {
  const response = await fetch(`${API_BASE_URL}/staff/roles`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    await raise(response);
  }

  const data = await response.json();
  return data.roles || [];
};

export const grantRole = async (userId, roleName) => {
  const response = await fetch(`${API_BASE_URL}/staff/users/${encodeURIComponent(userId)}/roles`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ role: roleName }),
  });

  if (!response.ok) {
    await raise(response, {
      404: 'That user or role no longer exists',
      409: 'That role is already granted manually',
    });
  }
};

export const revokeRole = async (userId, roleName) => {
  const response = await fetch(
    `${API_BASE_URL}/staff/users/${encodeURIComponent(userId)}/roles/${encodeURIComponent(roleName)}`,
    { method: 'DELETE', headers: getAuthHeaders() },
  );

  if (!response.ok) {
    await raise(response, {
      404: 'There is no manual grant of that role to revoke',
    });
  }
};

/**
 * Clear every second factor a user has enrolled — the break-glass path for
 * someone who has lost their authenticator.
 *
 * Deliberately here rather than in mfaService, because the two modules mean
 * opposite things by a 403. There it is a mistyped password or code, shown
 * inline with the flow left standing; here it is a missing can_manage_user_mfa,
 * which raise() turns into a ForbiddenError and the page renders as a 404. One
 * module holding both meanings would need a flag, and passing it wrong would
 * blank a half-finished enrollment.
 *
 * Note the asymmetry with the self-service path: unenrolling yourself demands a
 * password *and* a current code, while this demands neither — the operator's
 * permission is the whole check, and the server's audit log
 * (admin_ctrl.go:301-307) is the only other record. That is why the caller
 * gates it behind a typed confirmation.
 *
 * 401 and 403 are still bare strings by design; raise() discards both bodies
 * anyway, which is precisely why they were left unconverted.
 */
export const clearUserMfa = async (userId) => {
  const response = await fetch(
    `${API_BASE_URL}/admin/users/${encodeURIComponent(userId)}/mfa`,
    { method: 'DELETE', headers: getAuthHeaders() },
  );

  if (!response.ok) {
    await raise(response, {
      400: 'User not found',
      404: 'User not found',
    });
  }
};
